import React, { useState, useRef, useMemo } from 'react';
import '../Style/Register.css';
import {
  FaUser,
  FaPhone,
  FaEnvelope,
  FaLock,
  FaUserCog,
  FaBirthdayCake,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebaseConfig';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  setDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CompanyLogo from '../assets/CompanyLogo.png';

export default function Register() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    username: '',
    cellphone: '',
    role: '',
    email: '',
    password: '',
    confirmPassword: '',
    birthday: '', // <-- NEW
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  // Helpers
  const todayStr = useMemo(() => new Date().toISOString().split('T')[0], []);
  const minDobStr = '1900-01-01';

  const getAgeFromDob = (dobStr) => {
    if (!dobStr) return null;
    const dob = new Date(dobStr + 'T00:00:00');
    if (Number.isNaN(dob.getTime())) return null;
    const now = new Date();
    let age = now.getFullYear() - dob.getFullYear();
    const m = now.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age--;
    return age >= 0 && age < 130 ? age : null;
  };

  /* ───────── validation helpers ───────── */
  const validateField = (name, value) => {
    switch (name) {
      case 'firstName':
        return value.trim() ? '' : 'First name is required.';
      case 'lastName':
        return value.trim() ? '' : 'Surname is required.';
      case 'username':
        if (!value.trim()) return 'Username is required.';
        if (value.trim().length < 3) return 'Min 3 characters.';
        if (!/^[a-z0-9_.-]+$/i.test(value)) return 'Letters, numbers, . _ - only.';
        return '';
      case 'cellphone':
        if (!value.trim()) return 'Cellphone is required.';
        if (!/^\+?\d[\d\s-]{7,}$/.test(value))
          return 'Format example: +27 82 123 4567';
        return '';
      case 'role':
        return value ? '' : 'Select a role.';
      case 'email':
        if (!value.trim()) return 'Email is required.';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
          return 'Invalid email address.';
        return '';
      case 'password':
        if (!value) return 'Password required.';
        if (value.length < 6) return 'At least 6 characters.';
        return '';
      case 'confirmPassword':
        if (!value) return 'Confirm your password.';
        if (value !== form.password) return 'Passwords do not match.';
        return '';
      case 'birthday': {
        if (!value) return 'Birthday is required.';
        if (value < minDobStr) return 'Birthday is too far in the past.';
        if (value > todayStr) return 'Birthday cannot be in the future.';
        const age = getAgeFromDob(value);
        if (age == null) return 'Please enter a valid date.';
        // (Optional) enforce a minimum age, e.g., 13:
        // if (age < 13) return 'You must be at least 13 years old.';
        return '';
      }
      default:
        return '';
    }
  };

  /* Password strength (simple heuristic) */
  const passwordStrength = useMemo(() => {
    const p = form.password || '';
    let score = 0;
    if (p.length >= 6) score++;
    if (p.length >= 10) score++;
    if (/[A-Z]/.test(p)) score++;
    if (/[a-z]/.test(p)) score++;
    if (/\d/.test(p)) score++;
    if (/[^A-Za-z0-9]/.test(p)) score++;
    return Math.min(score, 5);
  }, [form.password]);

  const strengthLabel =
    ['Weak', 'Fair', 'Good', 'Strong', 'Robust', 'Elite'][passwordStrength] || 'Weak';

  const agePreview = useMemo(() => getAgeFromDob(form.birthday), [form.birthday]);

  /* field change with debounced validation */
  const handleChange = (e) => {
    const { id, value } = e.target;
    setForm((prev) => ({ ...prev, [id]: value }));
    setTouched((prev) => ({ ...prev, [id]: true }));

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const msg = validateField(id, value);
      setErrors((prev) => ({ ...prev, [id]: msg }));
      if (msg) toast.error(msg, { toastId: id });
      else toast.dismiss(id);
    }, 800);
  };

  const forceValidateAll = () => {
    const newErrors = {};
    Object.entries(form).forEach(([k, v]) => {
      const m = validateField(k, v);
      if (m) newErrors[k] = m;
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isFormValid = () => forceValidateAll();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!forceValidateAll()) {
      toast.error('Fix validation errors.');
      return;
    }
    setLoading(true);
    const { username, email, password, birthday, ...rest } = form;
    try {
      /* uniqueness check */
      const qUser = query(
        collection(db, 'users'),
        where('username', '==', username.trim())
      );
      const snap = await getDocs(qUser);
      if (!snap.empty) {
        toast.error('That username is already taken.', {
          toastId: 'username-taken',
        });
        setLoading(false);
        return;
      }

      const cred = await createUserWithEmailAndPassword(
        auth,
        email.trim(),
        password
      );
      const uid = cred.user.uid;

      const ageAtSignup = getAgeFromDob(birthday) ?? null;

      await setDoc(doc(db, 'users', uid), {
        ...rest,
        username: username.trim(),
        email: email.trim(),
        cellphone: form.cellphone.trim(),
        role: form.role,
        birthday: new Date(birthday), // Firestore stores as Timestamp
        ageAtSignup,                  // convenient snapshot; you can recompute later if needed
        authId: uid,
        createdAt: serverTimestamp(),
      });

      toast.success('Account created!', { toastId: 'created' });
      setTimeout(() => navigate('/login'), 1400);
    } catch (err) {
      console.error(err);
      toast.error(err.code || 'Registration failed.');
    } finally {
      setLoading(false);
    }
  };

  /* inline field error helper */
  const fieldError = (f) =>
    touched[f] && errors[f] ? (
      <div className="field-hint error" id={`${f}-error`} role="alert">
        {errors[f]}
      </div>
    ) : null;

  return (
    <div className="register-container">
      <ToastContainer position="top-right" autoClose={3000} theme="colored" />
      <div className="reg-card">
        {/* Art / Branding */}
        <aside className="reg-art" aria-label="Company branding">
          <div className="brand-stack">
            <img
              src={CompanyLogo}
              alt="Company Logo"
              className="brand-logo"
              draggable="false"
            />
            <div className="brand-block">
              <h1 className="brand-name">Nyabanga Engineering</h1>
              <p className="brand-line">Plot 35, Pretoria Rd, Benoni</p>
              <p className="brand-line">
                011 420-1537 &nbsp; | &nbsp; 082 471-9512
              </p>
              <p className="brand-line">
                <a href="mailto:info@nyabanga.co.za">info@nyabanga.co.za</a>
              </p>
            </div>
          </div>
          <div className="bg-pulse one" />
          <div className="bg-pulse two" />
        </aside>

        {/* Form */}
        <section
          className="reg-form-panel"
          aria-labelledby="register-heading"
        >
          <h2>CREATE ACCOUNT</h2>
          <p className="subtitle">Enter your details below</p>

          <form onSubmit={handleSubmit} noValidate>
            <div className="form-grid">
              {/* FIRST NAME */}
              <div className="form-group">
                <label htmlFor="firstName">Name</label>
                <div className="input-row">
                  <FaUser className="icon" aria-hidden="true" />
                  <input
                    id="firstName"
                    type="text"
                    value={form.firstName}
                    onChange={handleChange}
                    placeholder="John"
                    aria-required="true"
                    aria-describedby="firstName-error"
                  />
                </div>
                {fieldError('firstName')}
              </div>

              {/* LAST NAME */}
              <div className="form-group">
                <label htmlFor="lastName">Surname</label>
                <div className="input-row">
                  <FaUser className="icon" aria-hidden="true" />
                  <input
                    id="lastName"
                    type="text"
                    value={form.lastName}
                    onChange={handleChange}
                    placeholder="Doe"
                    aria-required="true"
                    aria-describedby="lastName-error"
                  />
                </div>
                {fieldError('lastName')}
              </div>

              {/* USERNAME */}
              <div className="form-group">
                <label htmlFor="username">Username</label>
                <div className="input-row">
                  <FaUserCog className="icon" aria-hidden="true" />
                  <input
                    id="username"
                    type="text"
                    value={form.username}
                    onChange={handleChange}
                    placeholder="jdoe"
                    autoComplete="off"
                    aria-required="true"
                    aria-describedby="username-error"
                  />
                </div>
                {fieldError('username')}
              </div>

              {/* CELLPHONE */}
              <div className="form-group">
                <label htmlFor="cellphone">Cellphone</label>
                <div className="input-row">
                  <FaPhone className="icon" aria-hidden="true" />
                  <input
                    id="cellphone"
                    type="tel"
                    value={form.cellphone}
                    onChange={handleChange}
                    placeholder="+27 82 123 4567"
                    aria-required="true"
                    aria-describedby="cellphone-error"
                  />
                </div>
                {fieldError('cellphone')}
              </div>

              {/* ROLE */}
              <div className="form-group">
                <label htmlFor="role">Role</label>
                <div className="input-row select-wrap">
                  <FaUserCog className="icon" aria-hidden="true" />
                  <select
                    id="role"
                    value={form.role}
                    onChange={handleChange}
                    aria-required="true"
                    aria-describedby="role-error"
                  >
                    <option value="">Select role</option>
                    <option>Admin</option>
                    <option>Financial-Manager</option>
                    <option>Foreman</option>
                    <option>Director</option>
                    <option>General</option>
                  </select>
                  <span className="chevron" aria-hidden="true">▾</span>
                </div>
                {fieldError('role')}
              </div>

              {/* EMAIL */}
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <div className="input-row">
                  <FaEnvelope className="icon" aria-hidden="true" />
                  <input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="you@example.com"
                    aria-required="true"
                    aria-describedby="email-error"
                  />
                </div>
                {fieldError('email')}
              </div>

              {/* BIRTHDAY (NEW) */}
              <div className="form-group">
                <label htmlFor="birthday">Birthday</label>
                <div className="input-row">
                  <FaBirthdayCake className="icon" aria-hidden="true" />
                  <input
                    id="birthday"
                    type="date"
                    value={form.birthday}
                    onChange={handleChange}
                    min={minDobStr}
                    max={todayStr}
                    aria-required="true"
                    aria-describedby="birthday-error birthday-hint"
                  />
                </div>
                <div className="field-hint" id="birthday-hint" aria-live="polite">
                  {agePreview != null ? `Age: ${agePreview} years` : 'Select your date of birth'}
                </div>
                {fieldError('birthday')}
              </div>

              {/* PASSWORD */}
              <div className="form-group full-span">
                <label htmlFor="password">Password</label>
                <div className="input-row">
                  <FaLock className="icon" aria-hidden="true" />
                  <input
                    id="password"
                    type="password"
                    value={form.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    aria-required="true"
                    aria-describedby="password-error password-strength"
                  />
                </div>
                {fieldError('password')}
                <div
                  className={`pw-strength pw-${passwordStrength}`}
                  id="password-strength"
                  aria-live="polite"
                >
                  <div className="bars">
                    {[0,1,2,3,4].map(i => (
                      <span
                        key={i}
                        className={i <= passwordStrength - 1 ? 'on' : ''}
                      />
                    ))}
                  </div>
                  <span className="label">{strengthLabel}</span>
                </div>
              </div>

              {/* CONFIRM PASSWORD */}
              <div className="form-group full-span">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <div className="input-row">
                  <FaLock className="icon" aria-hidden="true" />
                  <input
                    id="confirmPassword"
                    type="password"
                    value={form.confirmPassword}
                    onChange={handleChange}
                    placeholder="••••••••"
                    aria-required="true"
                    aria-describedby="confirmPassword-error"
                  />
                </div>
                {fieldError('confirmPassword')}
              </div>
            </div>

            <button
              type="submit"
              className="btn-submit"
              disabled={loading}
              aria-busy={loading}
            >
              {loading ? 'Registering…' : 'Register'}
            </button>
          </form>

          <div className="helper-links">
            <button
              type="button"
              className="link-btn"
              onClick={() => navigate('/login')}
            >
              Already have an account? Login
            </button>
          </div>
          <div className="sr-only" aria-live="assertive">
            {/* Screen reader live region */}
            {Object.values(errors).filter(Boolean).join('. ')}
          </div>
        </section>
      </div>
    </div>
  );
}
